/**
 * Step 3: Partitions (REQUIRED)
 *
 * Manage dataset partitions - at least 1 required
 */

import React, { useState } from 'react';
import { Plus, Trash2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import { useDatasetPartitions } from '@/features/datasets/store/datasetCreationStore';
import { validatePartitions } from '@/features/datasets/utils/datasetValidation';

/**
 * Partitions Step Component
 */
export const DatasetPartitionsStep: React.FC = () => {
  const { partitions, addPartition, removePartition, updatePartition } = useDatasetPartitions();
  const [newPartitionName, setNewPartitionName] = useState('');

  // Validate partitions
  const validationErrors = validatePartitions(partitions);
  const hasErrors = validationErrors.length > 0;

  /**
   * Handle add partition
   */
  const handleAddPartition = () => {
    const trimmed = newPartitionName.trim();
    if (!trimmed) return;

    // Check for duplicates
    const isDuplicate = partitions.some(
      (p) => p.name.toLowerCase() === trimmed.toLowerCase()
    );

    if (isDuplicate) {
      return;
    }

    // Check valid name
    if (!/^[a-zA-Z0-9_]+$/.test(trimmed)) {
      return;
    }

    addPartition(trimmed);
    setNewPartitionName('');
  };

  /**
   * Handle remove partition
   */
  const handleRemovePartition = (id: string) => {
    // Don't allow removing the last partition if it would leave 0
    if (partitions.length === 1) {
      return;
    }
    removePartition(id);
  };

  /**
   * Handle update partition name
   */
  const handleUpdatePartition = (id: string, name: string) => {
    updatePartition(id, name);
  };

  /**
   * Handle key press in new partition input
   */
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddPartition();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-4">Partitions</h2>
        <p className="text-muted-foreground text-sm">
          Define partition columns for your dataset. At least one partition is required.
        </p>
      </div>

      {/* Validation Errors */}
      {hasErrors && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <ul className="list-disc list-inside space-y-1">
              {validationErrors.map((error, index) => (
                <li key={index}>{error.message}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Existing Partitions List */}
      {partitions.length > 0 && (
        <div className="space-y-2">
          <Label>Configured Partitions ({partitions.length})</Label>
          <div className="space-y-2">
            {partitions.map((partition) => (
              <Card key={partition.id} className="p-3">
                <div className="flex items-center gap-3">
                  <Input
                    value={partition.name}
                    onChange={(e) => handleUpdatePartition(partition.id, e.target.value)}
                    placeholder="partition_name"
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemovePartition(partition.id)}
                    disabled={partitions.length === 1}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Add New Partition */}
      <div className="space-y-2">
        <Label htmlFor="new-partition">Add Partition</Label>
        <div className="flex gap-2">
          <Input
            id="new-partition"
            value={newPartitionName}
            onChange={(e) => setNewPartitionName(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="e.g., year, month, region"
            className="flex-1"
          />
          <Button onClick={handleAddPartition} disabled={!newPartitionName.trim()}>
            <Plus className="h-4 w-4 mr-2" />
            Add
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Use letters, numbers, and underscores only. Press Enter to add quickly.
        </p>
      </div>

      {/* Info Box */}
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <strong>Note:</strong> Partitions help organize and query your data efficiently.
          Common partition columns include date fields (year, month, day) or categorical
          fields (region, category).
        </AlertDescription>
      </Alert>
    </div>
  );
};
